import { useEffect, useRef } from 'react';
import { getServiceRegionsByIds, ServiceRegion } from '@/entities/order';
import { useMemo } from 'react';
import useSWR, { useSWRConfig } from 'swr';

interface UseServiceRegionsOptions {
  ids: string[];
}

interface UseServiceRegionsReturn {
  serviceRegions: ServiceRegion[];
  isLoading: boolean;
  error: Error | undefined;
}

export function useServiceRegions({ ids }: UseServiceRegionsOptions): UseServiceRegionsReturn {
  const { mutate } = useSWRConfig();
  const triggeredRef = useRef<string | null>(null);
  const uniqueIds = useMemo(() => Array.from(new Set(ids.filter((id) => id))), [ids]);

  const idsKey = uniqueIds.length > 0 ? [...uniqueIds].sort().join(',') : null;
  const swrKey = idsKey ? (['service-regions', idsKey] as const) : null;
  const {
    data: allRegions,
    isLoading,
    error,
  } = useSWR<ServiceRegion[]>(
    swrKey,
    uniqueIds.length > 0 ? () => getServiceRegionsByIds(uniqueIds) : null,
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
      revalidateOnMount: false,
      dedupingInterval: 3600000, // 1시간
    }
  );

  useEffect(() => {
    if (!swrKey || allRegions !== undefined || isLoading || error) return;
    if (triggeredRef.current === idsKey) return;
    triggeredRef.current = idsKey;
    mutate(swrKey);
  }, [swrKey, idsKey, allRegions, isLoading, error, mutate]);

  return {
    serviceRegions: allRegions || [],
    isLoading,
    error: error ? new Error(error.message || '서비스 구역 로드 실패') : undefined,
  };
}
